import { useState, useEffect } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useVoicemail, useSaveVoicemail, useDeleteVoicemail } from '../../lib/api/hooks-extension.js';
import { Icon } from '../../components/ui/Icon.js';
import { Empty } from '../../components/ui/Primitives.js';
import { Section, Field, Toggle, FormActions } from '../../components/ui/Form.js';
import type { Extension } from '@arcnet/contracts';
import { confirmAction } from '../../components/ui/Confirm.js';

export function VoicemailTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const can = useSession((s) => s.can);
  const box = useVoicemail(tenantId, ext.id);
  const save = useSaveVoicemail(tenantId, ext.id);
  const remove = useDeleteVoicemail(tenantId, ext.id);

  const [form, setForm] = useState({
    mailbox_number: ext.number,
    name: ext.display_name,
    email_to: '' as string,
    email_attach: true,
    delete_after_email: false,
    transcribe: false,
    max_message_sec: 300,
    greeting_mode: 'default' as 'default' | 'recorded' | 'tts',
    greeting_text: '',
    pin: '',
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const b = box.data;
    if (!b) return;
    setForm({
      mailbox_number: b.mailbox_number,
      name: b.name ?? ext.display_name,
      email_to: (b.email_to ?? []).join(', '),
      email_attach: b.email_attach,
      delete_after_email: b.delete_after_email,
      transcribe: b.transcribe,
      max_message_sec: b.max_message_sec,
      greeting_mode: b.greeting_mode,
      greeting_text: b.greeting_text ?? '',
      pin: '',
    });
    setDirty(false);
  }, [box.data, ext.display_name]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const submit = () => {
    const emails = form.email_to.split(',').map((e) => e.trim()).filter(Boolean);
    save.mutate({
      mailbox_number: form.mailbox_number,
      name: form.name || null,
      email_to: emails,
      email_attach: form.email_attach,
      delete_after_email: form.delete_after_email,
      transcribe: form.transcribe,
      max_message_sec: form.max_message_sec,
      greeting_mode: form.greeting_mode,
      greeting_text: form.greeting_text || null,
      // Omitted entirely when blank, so saving other settings does not clear
      // a PIN the user never touched.
      ...(form.pin ? { pin: form.pin } : {}),
    }, { onSuccess: () => { setDirty(false); setForm((f) => ({ ...f, pin: '' })); } });
  };

  if (box.isLoading) {
    return <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 70 }} /></div>;
  }

  // A null box is a normal state, not a failure: not every extension has one.
  if (!box.data && !dirty) {
    return (
      <div className="arc-card">
        <Empty
          icon="media"
          title="No mailbox on this extension"
          hint="Callers who reach a busy or unanswered extension currently hear ringing until the call clears."
          action={
            <button type="button" className="arc-btn arc-btn-primary" onClick={() => setDirty(true)}>
              <Icon name="plus" size={13} /> Create mailbox
            </button>
          }
        />
      </div>
    );
  }

  return (
    <>
      <Section title="Mailbox">
        <Field label="Mailbox number" htmlFor="vm-num"
          hint="Identifies the mailbox. Messages are deposited against the extension itself, so this only differs from the extension number for reporting.">
          <input id="vm-num" className="arc-input arc-mono" value={form.mailbox_number}
            onChange={(e) => set('mailbox_number', e.target.value)} />
        </Field>
        <Field label="Mailbox name" htmlFor="vm-name">
          <input id="vm-name" className="arc-input" value={form.name}
            onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Maximum message length" htmlFor="vm-max" hint="Seconds.">
          <input id="vm-max" className="arc-input" inputMode="numeric"
            value={String(form.max_message_sec)}
            onChange={(e) => set('max_message_sec', Number(e.target.value) || 300)} />
        </Field>
        <Field label="PIN" htmlFor="vm-pin"
          hint={box.data?.has_pin
            ? 'A PIN is set. Enter a new one to replace it; leave blank to keep it.'
            : 'Digits only. Stored for when collection by phone is available — messages arrive by email today.'}>
          <input id="vm-pin" className="arc-input" type="password" inputMode="numeric"
            value={form.pin} placeholder={box.data?.has_pin ? '••••' : ''}
            onChange={(e) => set('pin', e.target.value)} />
        </Field>
      </Section>

      <Section title="Delivery" description="Where messages go once left.">
        <Field label="Email to" htmlFor="vm-email" span
          hint="Comma-separated. Leave blank to keep messages on the system only.">
          <input id="vm-email" className="arc-input" value={form.email_to}
            placeholder="sarah@meridian.co.za, reception@meridian.co.za"
            onChange={(e) => set('email_to', e.target.value)} />
        </Field>
        <Toggle id="vm-attach" label="Attach the audio file"
          hint="Off sends a notification only, which suits mailboxes with confidential content."
          checked={form.email_attach} onChange={(v) => set('email_attach', v)} />
        <Toggle id="vm-del" label="Delete after emailing"
          hint="Keeps nothing on the platform. Reduces retention exposure but there is no second copy if the email is lost."
          checked={form.delete_after_email} onChange={(v) => set('delete_after_email', v)} />
        <Toggle id="vm-tr" label="Transcribe messages"
          hint="Not yet available. The setting is saved and takes effect once transcription is switched on."
          checked={form.transcribe} onChange={(v) => set('transcribe', v)} />
      </Section>

      <Section title="Greeting" columns={1}
        description="Not yet played to callers — the switch uses its standard greeting. Saved here ready for when custom greetings land.">
        <Field label="Greeting" htmlFor="vm-greet">
          <select id="vm-greet" className="arc-select" value={form.greeting_mode}
            onChange={(e) => set('greeting_mode', e.target.value as typeof form.greeting_mode)}>
            <option value="default">System default</option>
            <option value="recorded">Recorded by the user</option>
            <option value="tts">Text to speech</option>
          </select>
        </Field>
        {form.greeting_mode === 'tts' && (
          <Field label="Greeting text" htmlFor="vm-text"
            hint="Spoken to the caller before the beep.">
            <textarea id="vm-text" className="arc-input" rows={3} value={form.greeting_text}
              onChange={(e) => set('greeting_text', e.target.value)}
              placeholder="You have reached Sarah at Meridian Logistics. Please leave a message." />
          </Field>
        )}
      </Section>

      {/*
        Removing the mailbox, not just clearing its fields.

        ABOVE the save bar, because FormActions is sticky with a z-index: while
        stuck it is displaced down from its place in the flow and paints over
        whatever follows it. This card was only visible because the page happens
        to scroll far enough for the bar to settle back; a shorter form or a
        taller window covers it. A sticky save bar has to be the last thing in
        its scroll container.
        A saved-but-empty mailbox still answers unanswered calls and records into
        it; deleting is the only thing that puts the extension back to ringing
        out. Kept away from Save, and only offered once a mailbox exists.
      */}
      {box.data && can('voicemail:manage') && (
        <div className="arc-card" style={{ marginTop: 14 }}>
          <div className="arc-card-body arc-row" style={{ justifyContent: 'space-between', gap: 14 }}>
            <div className="arc-stack">
              <span style={{ fontWeight: 550 }}>Remove this mailbox</span>
              <span className="arc-subtle" style={{ fontSize: 12 }}>
                Unanswered calls will ring out instead of recording. Messages already
                stored on the switch are left alone.
              </span>
            </div>
            <button type="button" className="arc-btn arc-btn-danger"
              disabled={remove.isPending}
              onClick={() => {
                void confirmAction({
                  title: `Remove the mailbox on ${ext.number}?`,
                  body: 'Callers will no longer be able to leave a message.',
                  action: 'Remove', danger: true,
                }).then((ok) => { if (ok) remove.mutate(); });
              }}>
              {remove.isPending ? 'Removing…' : 'Remove mailbox'}
            </button>
          </div>
        </div>
      )}

      <FormActions
        dirty={dirty} saving={save.isPending}
        onReset={() => { setDirty(false); void box.refetch(); }}
        onSave={submit}
      />
    </>
  );
}
